import React, { useEffect, useState } from "react";
import { Search, Plus, Minus, ShoppingCart, Tag } from "lucide-react";
import { toast } from "sonner";
import { Link, useSearchParams } from "react-router-dom";
import B2BLayout from "../business/B2BLayout";
import { api } from "../api/client";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;

const BusinessShop = () => {
    const [params, setParams] = useSearchParams();
    const q = params.get("q") || "";
    const category = params.get("category") || "";
    const page = parseInt(params.get("page") || "1", 10);
    const [search, setSearch] = useState(q);
    const [data, setData] = useState({ items: [], total: 0, tier: "tier1", tier_discount_pct: 5 });
    const [cats, setCats] = useState([]);
    const [loading, setLoading] = useState(true);
    const [qtys, setQtys] = useState({});
    const [adding, setAdding] = useState({});

    useEffect(() => { api.get("/categories").then(({ data }) => setCats(data || [])).catch(() => {}); }, []);

    useEffect(() => {
        let m = true;
        const load = async () => {
            setLoading(true);
            try {
                const { data } = await api.get("/b2b/products", { params: { q, category, page, page_size: 18 } });
                if (m) setData(data);
            } catch {
                toast.error("Could not load wholesale catalog");
            } finally {
                if (m) setLoading(false);
            }
        };
        load();
        return () => { m = false; };
    }, [q, category, page]);

    const setF = (k, v) => {
        const p = new URLSearchParams(params);
        if (v) p.set(k, v); else p.delete(k);
        if (k !== "page") p.delete("page");
        setParams(p);
    };

    const adjust = (id, delta, min = 1) => {
        setQtys((s) => ({ ...s, [id]: Math.max(min, (s[id] || 1) + delta) }));
    };

    const add = async (item) => {
        const qty = qtys[item.id] || 1;
        try {
            setAdding((s) => ({ ...s, [item.id]: true }));
            await api.post("/b2b/cart/items", { product_id: item.id, qty });
            toast.success(`Added ${qty} × ${item.name}`);
        } catch (err) {
            const d = err?.response?.data?.detail;
            toast.error(typeof d === "string" ? d : d?.message || "Could not add to cart");
        } finally {
            setAdding((s) => ({ ...s, [item.id]: false }));
        }
    };

    const totalPages = Math.max(1, Math.ceil((data.total || 0) / 18));

    return (
        <B2BLayout title="Wholesale shop" subtitle="B2B catalogue" action={
            <Link to="/business/cart" className="inline-flex items-center gap-1.5 rounded-full bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800" data-testid="b2b-shop-cart-link">
                <ShoppingCart className="h-3.5 w-3.5" /> View cart
            </Link>
        }>
            {(account) => (
                <div className="space-y-5" data-testid="b2b-shop">
                    <div className="rounded-2xl border border-brand-200 bg-gradient-to-r from-brand-50 to-violet-50 p-4">
                        <p className="flex items-center gap-1.5 text-xs font-semibold text-brand-800">
                            <Tag className="h-3.5 w-3.5" /> {account?.discount_tier?.toUpperCase()} · {data.tier_discount_pct}% off retail across this catalogue
                        </p>
                    </div>

                    <div className="flex flex-wrap items-center gap-3">
                        <form onSubmit={(e) => { e.preventDefault(); setF("q", search.trim()); }} className="relative flex-1 max-w-sm">
                            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                            <input
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                placeholder="Search wholesale catalogue…"
                                className="w-full rounded-full border border-slate-200 bg-white pl-9 pr-3 py-2 text-sm outline-none focus:border-brand-400"
                                data-testid="b2b-shop-search"
                            />
                        </form>
                        <select value={category} onChange={(e) => setF("category", e.target.value)} className="rounded-full border border-slate-200 bg-white px-3 py-2 text-sm" data-testid="b2b-shop-category">
                            <option value="">All categories</option>
                            {cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                        </select>
                    </div>

                    {loading ? (
                        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                            {[1, 2, 3, 4, 5, 6].map((i) => <div key={i} className="h-40 skeleton rounded-2xl" />)}
                        </div>
                    ) : data.items.length === 0 ? (
                        <p className="rounded-2xl border border-dashed border-slate-200 bg-white p-12 text-center text-sm text-slate-500" data-testid="b2b-shop-empty">
                            No products match your filters.
                        </p>
                    ) : (
                        <>
                            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
                                {data.items.map((p) => {
                                    const qty = qtys[p.id] || 1;
                                    return (
                                        <div key={p.id} className="flex flex-col rounded-2xl border border-zinc-100 bg-white p-4 shadow-soft" data-testid={`b2b-shop-product-${p.id}`}>
                                            {p.images?.[0] && (
                                                <img src={p.images[0]} alt={p.name} className="mb-3 h-32 w-full rounded-xl object-cover" loading="lazy" />
                                            )}
                                            <p className="line-clamp-2 text-sm font-semibold text-zinc-900">{p.name}</p>
                                            <p className="mt-0.5 text-xs text-slate-500">{p.brand} · {p.pack_size}</p>
                                            <div className="mt-3 flex items-baseline gap-2">
                                                <span className="text-lg font-bold text-brand-700">{inr(p.wholesale_price)}</span>
                                                <span className="text-xs line-through text-slate-400">{inr(p.mrp)}</span>
                                                {p.savings_vs_mrp > 0 && <span className="ml-auto rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">Save {inr(p.savings_vs_mrp)}</span>}
                                            </div>
                                            <p className="mt-1 text-[10px] text-slate-400">GST {p.gst_percent}% · Stock {p.stock_qty}</p>
                                            <div className="mt-3 flex items-center gap-2">
                                                <div className="inline-flex items-center rounded-full border border-zinc-200">
                                                    <button onClick={() => adjust(p.id, -1)} className="p-1.5 text-zinc-500 hover:bg-zinc-50" data-testid={`b2b-shop-qty-dec-${p.id}`}>
                                                        <Minus className="h-3 w-3" />
                                                    </button>
                                                    <span className="min-w-[28px] text-center text-xs font-medium" data-testid={`b2b-shop-qty-${p.id}`}>{qty}</span>
                                                    <button onClick={() => adjust(p.id, 1)} className="p-1.5 text-zinc-500 hover:bg-zinc-50" data-testid={`b2b-shop-qty-inc-${p.id}`}>
                                                        <Plus className="h-3 w-3" />
                                                    </button>
                                                </div>
                                                <button onClick={() => add(p)} disabled={adding[p.id]} className="ml-auto inline-flex items-center gap-1 rounded-full bg-brand-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-800 disabled:opacity-50" data-testid={`b2b-shop-add-${p.id}`}>
                                                    <ShoppingCart className="h-3 w-3" /> Add
                                                </button>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>

                            {totalPages > 1 && (
                                <div className="flex items-center justify-center gap-2 pt-4">
                                    <button onClick={() => setF("page", String(Math.max(1, page - 1)))} disabled={page === 1} className="rounded-full border border-zinc-200 px-3 py-1.5 text-xs disabled:opacity-50">Prev</button>
                                    <span className="text-xs text-slate-500">Page {page} of {totalPages}</span>
                                    <button onClick={() => setF("page", String(Math.min(totalPages, page + 1)))} disabled={page >= totalPages} className="rounded-full border border-zinc-200 px-3 py-1.5 text-xs disabled:opacity-50">Next</button>
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
        </B2BLayout>
    );
};

export default BusinessShop;
